import Chart from 'react-apexcharts';
import { currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import { useE3HistoricalData } from '@src/modules/performance/context/reports-api-hooks';
import { Link, Text } from '@chakra-ui/react';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Trend in pavement and drainage renewal cost',
  content: [
    {
      title: 'Renewal cost by network lane km',
      description:
  <Text fontSize="inherit" mb={2}>
    The trend in the total NLTP funded (NLTF share plus local share) road renewal costs for road resurfacing, pavement rehabilitation, and kerb and channel drainage renewal per annum for the network. These are the renewal activities that have the most direct impact on the condition of sealed roads. The VfM and Efficiency calculations include the costs to date for the last three years.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools" isExternal>NZTA Data and Tools, Road maintenance, operation and renewals</Link>
    {' '}
    and
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools, Physical statistics - roads.</Link>
  </Text>,
    },
  ],
};

function E3Cost() {
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: historicalData, isLoading: isLoadingHistoricalData } = useE3HistoricalData();
  const isRegionNationalOrStateHighways = currentDatabase?.id === 81 || currentDatabase?.id >= 1000;

  const sortedHistoricalData = historicalData?.sort((a, b) => a.year - b.year);
  const years = [...new Set(sortedHistoricalData?.map((x) => x.year).flat())];

  const historicalLockYear = sortedHistoricalData?.at(-1)?.lockYear;

  const chartData = [
    {
      name: currentDatabase?.name,
      type: 'line',
      data: historicalData?.map((x) => (x.cost ? Math.round(x.cost * 1000) : null)) || [],
      color: CHART_COLOURS.orange,
    },
    {
      name: 'Peer Group',
      type: 'line',
      data: historicalData?.map((x) => (x.peerGroupCost ? Math.round(x.peerGroupCost * 1000) : null)) || [],
      color: CHART_COLOURS.black,
    },
  ];

  const chartSeries = isRegionNationalOrStateHighways
    ? [...chartData.filter((x) => !x.name?.includes('Peer'))]
    : [...chartData];

  return (
    <ReportSection
      title="Trend in pavement and drainage renewal cost"
      lockYear={historicalLockYear}
      isLoading={isLoadingHistoricalData}
      informationSource={informationSource}
    >
      <Chart
        options={{
          title: {
            text: 'Renewal cost by network lane km',
          },
          chart: {
            animations: {
              enabled: false,
            },
          },
          xaxis: {
            categories: years || [],
          },
          yaxis: [
            {
              min: 0,
              forceNiceScale: true,
              title: {
                text: '$ / lane km',
              },
              labels: {
                formatter(val) {
                  return `$${val.toFixed(0)}`;
                },
                offsetX: 0,
              },
            },
          ],
          grid: {
            padding: {
              left: 20,
            },
          },
          dataLabels: {
            enabled: false,
          },
          legend: {
            show: true,
          },
        }}
        series={chartSeries}
        type="line"
        width="100%"
        height="240"
      />
    </ReportSection>
  );
}

export default E3Cost;
